import { zodResolver } from '@hookform/resolvers/zod';
import { type QuoteDocument, type QuoteLine, type QuotePhoto } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  Input,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@kalos/ui';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import SignaturePad from 'react-signature-canvas';
import { z } from 'zod';

import { getLineDiscount } from '../discount';
import { groupBySystem } from '../photoGroups';

// Schema for the single-page form
const proposalSchema = z.object({
  acceptedServices: z.array(z.number()).min(1, 'Please select at least one service'),
  acceptTerms: z.boolean().refine((val) => val === true, 'You must accept the terms'),
  acceptedBy: z.string().min(1, 'Please enter your name'),
});

type ProposalFormData = z.infer<typeof proposalSchema>;

/** Guided (phones): the heading for photos not tied to a system, after the systems' groups. */
const GUIDED_OTHER_PHOTOS_LABEL = 'Other photos';

/** The proposal's terms and conditions, shown in full on desktop and behind a toggle when guided. */
const TERMS_TEXT =
  "The undersigned has authority to order the above labor and materials on behalf of the above named purchaser. The labor and materials described above have been completely and satisfactorily performed and furnished. Kalos Services is not liable for any defects in labor or material unless the purchaser gives written notice of such defects within 30 days from the date of this contract. Payment in full is due upon receipt of this invoice and payable upon completion of work. The purchaser should be aware that under Florida law, Kalos Services has the right to file a mechanics lien if payment is not made per the terms of this document. Purchaser agrees to pay all costs of collection including a reasonable attorney's fee, in case this contract is not paid in full when due and the same is placed in the hands of an attorney for collection, foreclosure or repossession, whether suit be brought or not. All delinquent payments shall bear a service charge of 2% per month until paid. Selling price of all parts and equipment are on an exchange basis with the purchasers old parts and equipment. Warranty on all parts per manufacturers warranty policies 90 day labor guarantee: only on that portion previously serviced. If my payment is by check and my check is returned for any reason, I authorize the merchant to electronically debit my account for the amount of this item plus any fees allowed by law";

interface CustomerProposalSinglePageJobDetailsProps {
  customer: {
    firstname?: string;
    lastname?: string;
    businessname?: string;
    email?: string;
    phone?: string;
  };
  preparedBy: {
    name: string;
    title?: string;
    email: string;
    phone: string;
  };
  companyInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  };
  property?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  services: QuoteLine[];
  technicalNotes?: string;
  quotePhotos?: QuotePhoto[];
  proposalValidity: {
    validUntil: Date;
    agreementDuration: Date;
  };
  quoteDocument?: QuoteDocument;
  onComplete: (data: {
    acceptedServices: number[];
    signature: string;
    proposedBy: string;
    acceptedBy: string;
    signatureDate: string;
  }) => void;
  isSubmitting?: boolean;
  discount?: string;
  discountCost?: string;
  taxRate?: string;
  /** Set only for the in-person walk-through dialog (EventPhoto evidence, selector parity). */
  walkthroughEvidencePhotos?: {
    id: number;
    src: string;
    fileName?: string;
    serviceItemId?: number;
    serviceItemName?: string;
  }[];
  walkthroughEvidenceLoading?: boolean;
  /** Walk-through dialog: parent swaps to full-screen ImageGallery (proposal hidden until back). */
  onWalkthroughEvidenceOpenImageGallery?: (index: number) => void;
  /**
   * Phones: one question per step (choose services, what we found, terms, sign) with Back and
   * Continue, instead of one long page. The content and what Approve sends are the same.
   */
  guided?: boolean;
  /** Guided: a worded Close in the header, since phones get no corner X. */
  onClose?: () => void;
}

export const CustomerProposalSinglePageJobDetails: FC<
  CustomerProposalSinglePageJobDetailsProps
> = ({
  customer,
  preparedBy,
  companyInfo,
  property,
  services,
  technicalNotes,
  quotePhotos,
  proposalValidity,
  quoteDocument,
  onComplete,
  isSubmitting = false,
  discount,
  discountCost,
  taxRate: taxRateProp,
  walkthroughEvidencePhotos,
  walkthroughEvidenceLoading,
  onWalkthroughEvidenceOpenImageGallery,
  guided = false,
  onClose,
}) => {
  const [signature, setSignature] = useState('');
  const [evidenceTab, setEvidenceTab] = useState<'grid' | 'view'>('grid');
  const [viewingEvidence, setViewingEvidence] = useState<{
    src: string;
    fileName?: string;
  } | null>(null);
  const signaturePadRef = useRef<SignaturePad>(null);
  const isSavingRef = useRef(false);

  // Store initial quote statuses
  const [initialQuoteStatuses] = useState<Record<number, number>>(() => {
    return services.reduce(
      (acc, service) => {
        acc[service.id] = service.quoteStatus || 0;
        return acc;
      },
      {} as Record<number, number>,
    );
  });

  const defaultSignedBy = useMemo(
    () =>
      [customer.firstname, customer.lastname].filter(Boolean).join(' ').trim() ||
      customer.businessname ||
      '',
    [customer.firstname, customer.lastname, customer.businessname],
  );

  const form = useForm<ProposalFormData>({
    resolver: zodResolver(proposalSchema),
    defaultValues: {
      acceptedServices: [
        // Automatically include already-approved services
        ...services
          .filter((service) => initialQuoteStatuses[service.id] === 1)
          .map((service) => service.id),
      ],
      acceptTerms: false,
      acceptedBy: defaultSignedBy,
    },
  });

  // Watch form values for button enable logic
  const acceptedServices = form.watch('acceptedServices');
  const acceptTerms = form.watch('acceptTerms');
  const acceptedBy = form.watch('acceptedBy');

  // Check if any NEW (non-approved) services have been selected

  // Calculate selected total
  const selectedSubtotal = services.reduce((sum, service) => {
    const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
    const isSelected = acceptedServices.includes(service.id);

    if (isSelected || isAlreadyApproved) {
      const price = parseFloat(service.adjustment || '0');
      const quantity = service.quotedQuantity || 1;
      return sum + price * quantity;
    }
    return sum;
  }, 0);

  // Calculate discount
  const discountPercentValue = parseFloat(discount || '0');
  const discountCostValue = parseFloat(discountCost || '0');
  const discountAmount =
    discountCostValue > 0
      ? discountCostValue
      : services.reduce((sum, service) => {
          const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
          const isSelected = acceptedServices.includes(service.id);
          if (!isSelected && !isAlreadyApproved) return sum;
          const lineTotal = parseFloat(service.adjustment || '0') * (service.quotedQuantity || 1);
          return (
            sum +
            getLineDiscount(
              lineTotal,
              discountPercentValue,
              discountCostValue,
              service.excludeInvoiceDiscount,
            )
          );
        }, 0);
  const afterDiscount = Math.max(0, selectedSubtotal - discountAmount);

  // Calculate tax
  const parsedTaxRate = parseFloat(taxRateProp || '0') || 0;
  const taxAmount = afterDiscount * (parsedTaxRate / 100);
  const selectedTotal = afterDiscount + taxAmount;

  // Dates
  const today = new Date();
  const daysBetween = (date1: Date, date2: Date) => {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return Math.max(0, Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));
  };
  const proposalValidityDays = daysBetween(today, proposalValidity.validUntil);
  const agreementDurationDays = daysBetween(today, proposalValidity.agreementDuration);

  // Get selectable services
  const selectableServices = services.filter((service) => initialQuoteStatuses[service.id] !== 1);
  const allSelectableSelected = selectableServices.every((service) =>
    acceptedServices.includes(service.id),
  );

  // Select All handler
  const handleSelectAll = useCallback(() => {
    const allServiceIds = services.map((service) => service.id);
    form.setValue('acceptedServices', allServiceIds);
  }, [services, form]);

  const usesWalkthroughEvidence =
    walkthroughEvidencePhotos !== undefined || walkthroughEvidenceLoading !== undefined;

  const evidenceUsesExternalImageGallery =
    usesWalkthroughEvidence && !!onWalkthroughEvidenceOpenImageGallery;

  const evidenceGalleryItems = useMemo(() => {
    if (usesWalkthroughEvidence) {
      return (walkthroughEvidencePhotos ?? []).map((p) => ({
        id: p.id,
        src: p.src,
        fileName: p.fileName,
        serviceItemId: p.serviceItemId ?? 0,
        serviceItemName: p.serviceItemName ?? '',
      }));
    }
    if (!Array.isArray(quotePhotos)) {
      return [];
    }
    return quotePhotos
      .filter((p): p is QuotePhoto & { downloadUrl: string } => Boolean(p.downloadUrl))
      .map((p) => ({
        id: p.photoId,
        src: p.downloadUrl,
        fileName: p.fileName,
        serviceItemId: 0,
        serviceItemName: '',
      }));
  }, [usesWalkthroughEvidence, walkthroughEvidencePhotos, quotePhotos]);

  // Group evidence by system (ServiceItem), keeping each item's flat index so
  // the external image gallery still opens at the right photo. Unassigned
  // photos fall into "Additional photos", rendered last.
  // Guided (phones) only: those unassigned photos are headed "Other photos" after the systems'
  // groups, and get no heading when they're all there is.
  const evidenceGroups = useMemo(() => {
    const groups = groupBySystem(
      evidenceGalleryItems.map((item, index) => ({ item, index })),
      (entry) => entry.item.serviceItemId,
      (entry) => entry.item.serviceItemName,
    );
    if (!guided) return groups;
    const hasSystemGroups = groups.some((group) => group.serviceItemId !== 0);
    return groups.map((group) =>
      group.serviceItemId === 0
        ? { ...group, label: hasSystemGroups ? GUIDED_OTHER_PHOTOS_LABEL : '' }
        : group,
    );
  }, [evidenceGalleryItems, guided]);

  const showEvidenceSection =
    quoteDocument?.includeAttachments === 1 &&
    (walkthroughEvidenceLoading === true || evidenceGalleryItems.length > 0);

  useEffect(() => {
    setEvidenceTab('grid');
    setViewingEvidence(null);
  }, [quoteDocument?.id]);

  // Guided: a photo opens on the whole screen with a worded Close, like the record's other
  // photo views, instead of the gallery with its toolbar.
  const [guidedPhoto, setGuidedPhoto] = useState<{ src: string; fileName?: string } | null>(null);

  const openEvidenceView = useCallback((item: { src: string; fileName?: string }) => {
    setViewingEvidence({ src: item.src, fileName: item.fileName });
    setEvidenceTab('view');
  }, []);

  const backToEvidenceGrid = useCallback(() => {
    setEvidenceTab('grid');
    setViewingEvidence(null);
  }, []);

  // Signature handlers
  const handleSignatureClear = useCallback(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setSignature('');
    }
  }, []);

  const handleSignatureSave = useCallback(() => {
    if (signaturePadRef.current) {
      isSavingRef.current = true;
      const signatureData = signaturePadRef.current.toDataURL('image/png', 1.0);
      setSignature(signatureData);
      setTimeout(() => {
        isSavingRef.current = false;
      }, 100);
    }
  }, []);

  // Restore signature
  useEffect(() => {
    if (signature && signaturePadRef.current && !isSavingRef.current) {
      const timeoutId = setTimeout(() => {
        if (signaturePadRef.current) {
          const canvas = signaturePadRef.current.getCanvas();
          const ctx = canvas.getContext('2d');

          if (!ctx) return;

          const { width, height } = canvas;
          signaturePadRef.current.clear();

          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
          };
          img.src = signature;
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [signature]);

  // Guided (phones): the steps, skipping "What we found" when there's nothing to show.
  const hasEvidenceStep =
    (!!technicalNotes && quoteDocument?.includeJobNotes === 1) || showEvidenceSection;
  const guidedSteps = (
    [
      // What we found comes first: the why before the price. Without notes or photos, choosing
      // the services is the first step.
      ...(hasEvidenceStep ? [['evidence', 'What we found']] : []),
      // Choosing the services, the terms and the signature are one step: one less tap, and the
      // agreement sits right under what's being agreed to.
      ['sign', 'Choose and sign'],
    ] as [string, string][]
  ).map(([id, label]) => ({ id, label }));
  const [guidedStepIndex, setGuidedStepIndex] = useState(0);
  const [showFullTerms, setShowFullTerms] = useState(false);
  // Guided: Approve stays tappable; a tap with something missing marks each missing piece and
  // scrolls to the first, instead of a greyed-out button that doesn't say why.
  const [showMissing, setShowMissing] = useState(false);
  const guidedStep = guidedSteps[Math.min(guidedStepIndex, guidedSteps.length - 1)];
  const isLastGuidedStep = guidedStepIndex >= guidedSteps.length - 1;
  const show = (section: string) => !guided || guidedStep.id === section;
  const guidedTopRef = useRef<HTMLDivElement>(null);
  const goToGuidedStep = (index: number) => {
    const next = Math.max(0, Math.min(index, guidedSteps.length - 1));
    // Leaving the sign step unmounts the pad, so the kept signature would sit behind a blank box.
    if (guidedSteps[next]?.id !== 'sign') setSignature('');
    setGuidedStepIndex(next);
    guidedTopRef.current?.scrollIntoView({ block: 'start' });
  };
  const canContinueGuided = true;

  // Check if approve button should be enabled
  const isApproveEnabled =
    acceptedServices.length > 0 &&
    acceptTerms &&
    acceptedBy.trim() !== '' &&
    signature !== '' &&
    !isSubmitting;

  const handleSubmit = useCallback(
    (data: ProposalFormData) => {
      if (!signature) {
        toast({
          title: 'Signature Required',
          description: 'Please provide your signature to continue.',
          variant: 'destructive',
        });
        return;
      }

      onComplete({
        acceptedServices: data.acceptedServices,
        signature,
        proposedBy: preparedBy.name,
        acceptedBy: data.acceptedBy,
        signatureDate: new Date().toISOString().split('T')[0],
      });
    },
    [signature, onComplete, preparedBy.name],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      {guided ? (
        <div className="bg-card flex flex-col gap-2 border-b px-4 py-3">
          {/* With one step there's nothing to count: just who it's from. */}
          <div className={guidedSteps.length > 1 ? 'flex gap-1' : 'hidden'} aria-hidden>
            {guidedSteps.map((step, index) => (
              <span
                key={step.id}
                className={`h-1 flex-1 rounded-full ${index <= guidedStepIndex ? 'bg-primary' : 'bg-border'}`}
              />
            ))}
          </div>
          <p className="text-muted-foreground text-xs">
            {[
              guidedSteps.length > 1 ? `Step ${guidedStepIndex + 1} of ${guidedSteps.length}` : '',
              companyInfo.name,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-semibold">{guidedStep.label}</h1>
            {onClose && (
              <Button type="button" variant="ghost" className="-mr-2" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          className="bg-primary border-b px-6 py-4 shadow-md"
          style={{ backgroundColor: 'hsl(var(--primary))', color: 'white' }}
        >
          <h1 className="text-2xl font-bold text-white">Customer Proposal Review</h1>
        </div>
      )}

      <FormContext {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          {/* Scrollable Content */}
          <ScrollArea className="flex-1">
            <div className="w-full overflow-x-hidden p-4 sm:p-6">
              <div ref={guidedTopRef} />
              {!guided && (
                <>
                  {/* Top Alert */}
                  <div
                    className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4"
                    style={{
                      backgroundColor: 'rgb(239 246 255)',
                      borderColor: 'rgb(219 234 254)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-info mt-0.5 h-5 w-5 flex-shrink-0"
                        style={{ color: 'rgb(29 78 216)' }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 16v-4"></path>
                        <path d="M12 8h.01"></path>
                      </svg>
                      <div>
                        <h3 className="mb-1 font-medium" style={{ color: 'rgb(29 78 216)' }}>
                          Review Your Proposal
                        </h3>
                        <p className="text-sm" style={{ color: 'rgb(29 78 216)' }}>
                          Please review all details below, confirm each service by checking the
                          boxes, accept the terms, and sign to complete your proposal.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Client & Company Information */}
                  <Card className="mb-6 bg-white p-4">
                    <div className="mb-4 flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-users mr-2 h-6 w-6 text-gray-600"
                      >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <h2 className="text-xl font-semibold">Client & Company Information</h2>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Client Information */}
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <h3 className="mb-3 text-lg font-medium">Client Information</h3>
                          <div className="space-y-2">
                            <p className="break-words">
                              <span className="font-medium">Name:</span>{' '}
                              {customer.businessname
                                ? `${customer.businessname} (${customer.firstname} ${customer.lastname})`
                                : `${customer.firstname} ${customer.lastname}`}
                            </p>
                            <p className="break-words">
                              <span className="font-medium">Email:</span> {customer.email}
                            </p>
                            <p className="break-words">
                              <span className="font-medium">Phone:</span> {customer.phone}
                            </p>
                            {property && (
                              <p className="break-words">
                                <span className="font-medium">Site Address:</span>{' '}
                                {[property.address, property.city, property.state, property.zip]
                                  .filter(Boolean)
                                  .join(', ')}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Prepared By */}
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <h3 className="mb-3 text-lg font-medium">Prepared By</h3>
                          <div className="space-y-2">
                            <p className="break-words">
                              <span className="font-medium">Name:</span> {preparedBy.name}
                            </p>
                            {preparedBy.title && (
                              <p className="break-words">
                                <span className="font-medium">Title:</span> {preparedBy.title}
                              </p>
                            )}
                            <p className="break-words">
                              <span className="font-medium">Email:</span> office@kalosflorida.com
                            </p>
                            <p className="break-words">
                              <span className="font-medium">Phone:</span> (352) 243-7099
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Company Information */}
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <h3 className="mb-3 text-lg font-medium">Company Information</h3>
                        <div>
                          <h4 className="break-words text-lg font-semibold">{companyInfo.name}</h4>
                          <div className="mt-2 space-y-1 text-sm">
                            <p className="break-words">{companyInfo.address}</p>
                            <p className="break-words">Phone: {companyInfo.phone}</p>
                            <p className="break-words">Email: {companyInfo.email}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </>
              )}

              {show('evidence') && (
                <>
                  {/* Technical Notes — always visible for customer */}
                  {technicalNotes &&
                    quoteDocument?.includeJobNotes === 1 &&
                    (guided ? (
                      // Guided: the step is already titled What we found; just the words.
                      <p className="mb-4 whitespace-pre-wrap break-words text-base">
                        {technicalNotes}
                      </p>
                    ) : (
                      <Card className="mb-6 bg-white p-4">
                        <h2 className="mb-4 text-xl font-semibold">Technician Notes</h2>
                        <div className="whitespace-pre-wrap break-words text-sm">
                          {technicalNotes}
                        </div>
                      </Card>
                    ))}

                  {/* Supporting Documentation / evidence (quote-line URLs or walk-through EventPhoto set) */}
                  {showEvidenceSection && (
                    <Card
                      className={
                        guided
                          ? 'mb-4 border-0 bg-transparent p-0 shadow-none'
                          : 'mb-6 bg-white p-4'
                      }
                    >
                      {!guided && (
                        <h3 className="mb-4 text-xl font-bold">Supporting Documentation</h3>
                      )}
                      {walkthroughEvidenceLoading ? (
                        <div className="flex flex-col items-center justify-center gap-2 py-10 text-sm text-gray-500">
                          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                          <span>Loading photos…</span>
                        </div>
                      ) : evidenceUsesExternalImageGallery ? (
                        <div className="flex flex-col gap-6">
                          {evidenceGroups.map((group) => (
                            <div key={group.serviceItemId}>
                              {group.label && (
                                <h4 className="mb-2 text-lg font-semibold">{group.label}</h4>
                              )}
                              <div
                                className={
                                  guided
                                    ? 'grid grid-cols-3 gap-2'
                                    : 'grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
                                }
                              >
                                {group.items.map(({ item, index }) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    className="flex items-center justify-center rounded border border-gray-200 p-2 text-left shadow transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onClick={() =>
                                      guided && item.src
                                        ? setGuidedPhoto({ src: item.src, fileName: item.fileName })
                                        : onWalkthroughEvidenceOpenImageGallery?.(index)
                                    }
                                  >
                                    {item.src ? (
                                      <img
                                        src={item.src}
                                        alt={item.fileName || ''}
                                        className="h-28 w-full rounded object-contain"
                                        style={{
                                          objectFit: 'contain',
                                          background: '#fff',
                                          maxHeight: '7rem',
                                        }}
                                      />
                                    ) : (
                                      <span className="flex h-28 w-full items-center justify-center rounded bg-gray-100 px-1 text-center text-xs text-gray-600">
                                        View photo
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Tabs
                          value={evidenceTab}
                          onValueChange={(v) => {
                            const next = v as 'grid' | 'view';
                            if (next === 'grid') {
                              backToEvidenceGrid();
                            } else if (viewingEvidence) {
                              setEvidenceTab('view');
                            }
                          }}
                        >
                          <TabsList className="mb-4 grid w-full max-w-md grid-cols-2">
                            <TabsTrigger value="grid">All photos</TabsTrigger>
                            <TabsTrigger value="view" disabled={!viewingEvidence}>
                              Full size
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="grid" className="mt-0 outline-none">
                            <div className="flex flex-col gap-6">
                              {evidenceGroups.map((group) => (
                                <div key={group.serviceItemId}>
                                  {group.label && (
                                    <h4 className="mb-2 text-lg font-semibold">{group.label}</h4>
                                  )}
                                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                                    {group.items.map(({ item }) => (
                                      <button
                                        key={item.id}
                                        type="button"
                                        className="flex items-center justify-center rounded border border-gray-200 p-2 text-left shadow transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onClick={() =>
                                          guided && item.src
                                            ? setGuidedPhoto({
                                                src: item.src,
                                                fileName: item.fileName,
                                              })
                                            : openEvidenceView(item)
                                        }
                                      >
                                        {item.src ? (
                                          <img
                                            src={item.src}
                                            alt={item.fileName || ''}
                                            className="h-28 w-full rounded object-contain"
                                            style={{
                                              objectFit: 'contain',
                                              background: '#fff',
                                              maxHeight: '7rem',
                                            }}
                                          />
                                        ) : (
                                          <span className="flex h-28 w-full items-center justify-center rounded bg-gray-100 px-1 text-center text-xs text-gray-600">
                                            View photo
                                          </span>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                          <TabsContent value="view" className="mt-0 outline-none">
                            {viewingEvidence ? (
                              <div className="flex flex-col gap-3">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="w-fit"
                                  onClick={backToEvidenceGrid}
                                >
                                  ← Back to all photos
                                </Button>
                                <div className="flex min-h-[min(82dvh,920px)] items-center justify-center overflow-auto rounded-lg border border-gray-200 bg-neutral-50 p-2">
                                  <img
                                    src={viewingEvidence.src}
                                    alt={viewingEvidence.fileName || 'Evidence'}
                                    className="max-h-[min(80dvh,880px)] w-full max-w-full object-contain"
                                  />
                                </div>
                              </div>
                            ) : null}
                          </TabsContent>
                        </Tabs>
                      )}
                    </Card>
                  )}
                </>
              )}

              {guided && guidedStep.id === 'sign' && (
                // Guided: one row per service, the price on the right, quantity only when it's
                // more than one, and the total at the bottom. Nothing to decode.
                <div className="flex flex-col gap-3" id="guided-services">
                  {services.filter((service) => initialQuoteStatuses[service.id] !== 1).length >
                    1 &&
                    !allSelectableSelected && (
                      // On the list's own heading line, not a row of space above it.
                      <div className="-mb-2 flex items-center justify-between">
                        <span className="text-muted-foreground text-sm">Services</span>
                        <Button
                          type="button"
                          variant="link"
                          className="text-link h-auto min-h-11 p-0 font-normal underline"
                          onClick={handleSelectAll}
                        >
                          Select all
                        </Button>
                      </div>
                    )}
                  <ul className="bg-card divide-border divide-y rounded-lg border">
                    {services.map((service) => {
                      const price = parseFloat(service.adjustment || '0');
                      const quantity = service.quotedQuantity || 1;
                      const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
                      const isChosen = acceptedServices.includes(service.id);
                      return (
                        <li key={service.id}>
                          <label className="flex min-h-14 cursor-pointer items-start gap-3 px-4 py-3">
                            <Checkbox
                              aria-label={`Accept ${service.description}`}
                              className="mt-0.5 shrink-0"
                              checked={isChosen}
                              disabled={isAlreadyApproved}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  'acceptedServices',
                                  checked
                                    ? [...acceptedServices, service.id]
                                    : acceptedServices.filter((id) => id !== service.id),
                                  { shouldValidate: true },
                                )
                              }
                            />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="break-words text-sm font-medium">
                                {service.description}
                              </span>
                              {(quantity > 1 || isAlreadyApproved) && (
                                <span className="text-muted-foreground text-xs">
                                  {[
                                    quantity > 1 ? `${quantity} × $${price.toFixed(2)}` : '',
                                    isAlreadyApproved ? 'Already approved' : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 text-sm font-semibold tabular-nums">
                              ${(price * quantity).toFixed(2)}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="bg-card flex flex-col gap-1 rounded-lg border px-4 py-3">
                    {(discountAmount > 0 || parsedTaxRate > 0) && (
                      <div className="text-muted-foreground flex justify-between text-sm">
                        <span>Services</span>
                        <span className="tabular-nums">${selectedSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {discountAmount > 0 && (
                      <div className="text-muted-foreground flex justify-between text-sm">
                        <span>Discount</span>
                        <span className="tabular-nums">−${discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {parsedTaxRate > 0 && (
                      <div className="text-muted-foreground flex justify-between text-sm">
                        <span>Tax</span>
                        <span className="tabular-nums">${taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-semibold">Your total</span>
                      <span className="text-lg font-semibold tabular-nums">
                        ${selectedTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {showMissing && acceptedServices.length === 0 && (
                    <p className="text-destructive text-sm">Choose at least one service.</p>
                  )}
                </div>
              )}

              {!guided && show('services') && (
                <>
                  {/* Proposed Solutions */}
                  <Card
                    className={
                      guided
                        ? 'mb-6 overflow-hidden bg-white p-4'
                        : 'mb-6 overflow-hidden border-l-4 bg-white p-4'
                    }
                    style={guided ? undefined : { borderLeftColor: 'rgb(59 130 246)' }}
                  >
                    <div className="mb-4 flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-briefcase mr-2 h-6 w-6"
                        style={{ color: 'hsl(var(--primary))' }}
                      >
                        <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        <rect width="20" height="14" x="2" y="6" rx="2"></rect>
                      </svg>
                      <h2 className="text-xl font-semibold">Proposed Solutions</h2>
                    </div>

                    {acceptedServices.length === 0 && (
                      <div className="mb-4">
                        <div
                          className="flex items-center rounded border px-4 py-2"
                          style={{
                            backgroundColor: 'rgb(255 251 235)',
                            borderColor: 'rgb(253 230 138)',
                            color: 'rgb(146 64 14)',
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-info mr-2 h-5 w-5 flex-shrink-0"
                            style={{ color: 'rgb(146 64 14)' }}
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 16v-4"></path>
                            <path d="M12 8h.01"></path>
                          </svg>
                          <span>
                            You must check at least one service to confirm your selection before
                            proceeding.
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mb-4 flex justify-end">
                      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                        <Checkbox
                          checked={allSelectableSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleSelectAll();
                            }
                          }}
                          style={
                            guided
                              ? undefined
                              : {
                                  borderColor: '#22c55e',
                                  backgroundColor: allSelectableSelected
                                    ? '#22c55e'
                                    : 'transparent',
                                }
                          }
                          className={guided ? undefined : 'rounded text-white'}
                        />
                        <span>Select all Unselected Services</span>
                      </label>
                    </div>

                    <div className="space-y-3">
                      {services.map((service) => {
                        const price = parseFloat(service.adjustment || '0');
                        const quantity = service.quotedQuantity || 1;
                        const totalPrice = price * quantity;
                        const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
                        const lineDiscount = getLineDiscount(
                          totalPrice,
                          discountPercentValue,
                          discountCostValue,
                          service.excludeInvoiceDiscount,
                        );
                        return (
                          <div
                            key={service.id}
                            className={`rounded border bg-white p-4 ${isAlreadyApproved ? 'opacity-60' : ''}`}
                          >
                            <div className="mb-3 flex items-start gap-3">
                              <FormField
                                control={form.control}
                                name="acceptedServices"
                                render={({ field }) => (
                                  <FormItem className="flex items-center space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        aria-label={`Accept ${service.description}`}
                                        checked={field.value.includes(service.id)}
                                        disabled={isAlreadyApproved}
                                        onCheckedChange={(checked) => {
                                          const newValue = checked
                                            ? [...field.value, service.id]
                                            : field.value.filter((id) => id !== service.id);
                                          field.onChange(newValue);
                                        }}
                                        style={
                                          guided
                                            ? undefined
                                            : {
                                                borderColor: '#22c55e',
                                                backgroundColor: field.value.includes(service.id)
                                                  ? '#22c55e'
                                                  : 'transparent',
                                              }
                                        }
                                        className={
                                          guided
                                            ? 'mt-0.5 shrink-0'
                                            : 'mt-0.5 shrink-0 rounded text-white'
                                        }
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="whitespace-pre-line break-words text-sm font-medium">
                                  {service.description}
                                </div>
                                {isAlreadyApproved && (
                                  <div className="mt-1 text-xs font-semibold text-green-600">
                                    (Already Approved)
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-6 text-sm">
                              <div>
                                <span className="text-gray-600">Qty:</span>
                                <span className="ml-2 font-medium">{quantity}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Unit Price:</span>
                                <span className="ml-2 font-medium">${price.toFixed(2)}</span>
                              </div>
                              {lineDiscount > 0 && (
                                <div>
                                  <span className="font-medium text-green-700">
                                    Discount -${lineDiscount.toFixed(2)}
                                  </span>
                                </div>
                              )}
                              <div>
                                <span className="text-gray-600">Total:</span>
                                <span className="ml-2 font-semibold">${totalPrice.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div className="space-y-2 rounded border border-gray-300 bg-gray-100 p-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">Subtotal</span>
                          <span className="font-medium">${selectedSubtotal.toFixed(2)}</span>
                        </div>
                        {(discountCostValue > 0 || discountPercentValue > 0) &&
                          discountAmount > 0 && (
                            <div className="flex items-center justify-between text-sm text-gray-600">
                              <span>{discountCostValue > 0 ? 'Discount' : 'Total discount'}</span>
                              <span>-${discountAmount.toFixed(2)}</span>
                            </div>
                          )}
                        {parsedTaxRate > 0 && (
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span>Tax ({parsedTaxRate}%)</span>
                            <span>${taxAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between border-t pt-2">
                          <span className="text-lg font-bold">Grand Total</span>
                          <span className="text-xl font-bold">${selectedTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </>
              )}

              {guided && guidedStep.id === 'sign' && (
                // Guided: one row, the box with the terms as a link that opens them; the dates
                // as a quiet line under it.
                // The same card as the services and total above it.
                <div
                  className={`bg-card mt-3 flex flex-col gap-0.5 rounded-lg border px-4 py-1.5 ${showMissing && !acceptTerms ? 'border-destructive' : ''}`}
                  id="guided-terms"
                >
                  <div className="flex min-h-11 items-center gap-3">
                    <Checkbox
                      id="guided-accept-terms"
                      aria-label="I accept the terms and conditions"
                      className={showMissing && !acceptTerms ? 'border-destructive' : undefined}
                      checked={acceptTerms}
                      onCheckedChange={(checked) =>
                        form.setValue('acceptTerms', checked === true, { shouldValidate: true })
                      }
                    />
                    <span className="text-sm">
                      <label htmlFor="guided-accept-terms" className="cursor-pointer">
                        I accept the{' '}
                      </label>
                      <button
                        type="button"
                        className="text-link min-h-11 underline"
                        onClick={() => setShowFullTerms(true)}
                      >
                        terms and conditions
                      </button>
                      .
                    </span>
                  </div>
                  {showMissing && !acceptTerms && (
                    <p className="text-destructive text-sm">Accept the terms to approve.</p>
                  )}
                  <p className="text-muted-foreground pb-1.5 text-xs">
                    Valid until {proposalValidity.validUntil.toLocaleDateString()} · agreement ends{' '}
                    {proposalValidity.agreementDuration.toLocaleDateString()}
                  </p>
                  <Sheet open={showFullTerms} onOpenChange={setShowFullTerms}>
                    <SheetContent
                      side="bottom"
                      className="max-h-[85svh] overflow-y-auto rounded-t-xl"
                      closeButtonClassName="hidden"
                    >
                      <SheetHeader className="mb-2 text-left">
                        <SheetTitle>Terms and conditions</SheetTitle>
                      </SheetHeader>
                      <p className="text-muted-foreground whitespace-pre-line text-sm">
                        {TERMS_TEXT}
                      </p>
                      <Button
                        type="button"
                        className="mt-4 w-full"
                        onClick={() => setShowFullTerms(false)}
                      >
                        Done
                      </Button>
                    </SheetContent>
                  </Sheet>
                </div>
              )}

              {!guided && show('terms') && (
                <>
                  {/* Agreement Timeline */}
                  <Card
                    className={guided ? 'mb-6 bg-white p-4' : 'mb-6 border-l-4 bg-white p-4'}
                    style={guided ? undefined : { borderLeftColor: 'hsl(var(--primary))' }}
                  >
                    <div className="mb-4 flex items-center">
                      <svg
                        className="mr-2 h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        style={{ color: 'hsl(var(--primary))' }}
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                      <h2 className="text-xl font-semibold">Agreement Timeline</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="flex h-full flex-col justify-between rounded-lg border p-4">
                        <div className="mb-2 flex items-center">
                          <svg
                            className="mr-2 h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            style={{ color: 'hsl(var(--primary))' }}
                          >
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <path d="M16 2v4M8 2v4M3 10h18" />
                          </svg>
                          <span className="font-medium">Proposal Validity</span>
                        </div>
                        <div className="mb-2 flex items-end gap-2">
                          <span className="text-3xl font-bold">{proposalValidityDays}</span>
                          <span className="text-gray-500">days</span>
                        </div>
                        <div
                          className="w-fit rounded px-2 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: 'hsl(var(--primary) / 0.1)',
                            color: 'hsl(var(--primary))',
                          }}
                        >
                          Expires on: {proposalValidity.validUntil.toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex h-full flex-col justify-between rounded-lg border p-4">
                        <div className="mb-2 flex items-center">
                          <svg
                            className="mr-2 h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            style={{ color: 'hsl(var(--primary))' }}
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9 12l2 2 4-4" />
                          </svg>
                          <span className="font-medium">Agreement Duration</span>
                        </div>
                        <div className="mb-2 flex items-end gap-2">
                          <span className="text-3xl font-bold">{agreementDurationDays}</span>
                          <span className="text-gray-500">days</span>
                        </div>
                        <div
                          className="w-fit rounded px-2 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: 'hsl(var(--primary) / 0.1)',
                            color: 'hsl(var(--primary))',
                          }}
                        >
                          Ends on: {proposalValidity.agreementDuration.toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Terms & Conditions */}
                  <Card className="mb-6 bg-white p-4" id="terms-section">
                    <h2 className="mb-4 text-xl font-semibold">Terms & Conditions</h2>
                    <div className="mb-4 max-h-60 overflow-y-auto rounded border p-4">
                      <div className="prose max-w-none text-sm">
                        <h3 className="mb-3 font-bold">TERMS AND CONDITIONS</h3>

                        <p className="mb-0">{TERMS_TEXT}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <FormField
                        control={form.control}
                        name="acceptTerms"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                aria-label="I accept the terms and conditions"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <label className="text-sm font-medium">
                              I accept the terms and conditions
                            </label>
                          </FormItem>
                        )}
                      />
                    </div>
                  </Card>
                </>
              )}

              {show('sign') && (
                <>
                  {/* Signature Section */}
                  <Card
                    className={
                      guided
                        ? // The same card as the services, total and terms: border, no shadow.
                          'bg-card mb-6 mt-3 rounded-lg border p-4 shadow-none'
                        : 'mb-6 bg-white p-4'
                    }
                    id="signature-section"
                  >
                    {!guided && (
                      <h2 className="mb-4 text-xl font-semibold">
                        Signature <span className="text-red-600">*</span>
                      </h2>
                    )}
                    <div
                      className={
                        guided
                          ? `relative mb-1 overflow-hidden rounded-md border ${showMissing && !signature ? 'border-destructive' : ''}`
                          : 'mb-4 overflow-hidden rounded border p-2'
                      }
                      id="signature-canvas"
                    >
                      {guided && !signature && (
                        <span className="text-muted-foreground pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-sm">
                          Sign here
                        </span>
                      )}
                      {guided && (
                        // Clear sits in the pad's corner, not on a row of its own.
                        <button
                          type="button"
                          onClick={handleSignatureClear}
                          className="text-link absolute right-1 top-1 z-10 min-h-11 px-2 text-sm underline"
                        >
                          Clear
                        </button>
                      )}
                      <SignaturePad
                        ref={signaturePadRef}
                        canvasProps={{
                          className: guided ? 'w-full' : 'border rounded w-full',
                          style: {
                            touchAction: 'none',
                            willChange: 'transform',
                            WebkitUserSelect: 'none',
                            WebkitTouchCallout: 'none',
                            WebkitTapHighlightColor: 'transparent',
                            width: '100%',
                            height: '200px',
                            display: 'block',
                          },
                        }}
                        onEnd={handleSignatureSave}
                        clearOnResize={false}
                        dotSize={1}
                        minWidth={0.5}
                        maxWidth={2.5}
                        throttle={16}
                        backgroundColor="rgba(255,255,255,1)"
                      />
                    </div>
                    {guided && showMissing && !signature && (
                      <p className="text-destructive mb-2 text-sm">Sign in the box above.</p>
                    )}
                    <div className={guided ? 'hidden' : 'mb-4 flex justify-end'}>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSignatureClear}
                        className="bg-white"
                      >
                        Clear Signature
                      </Button>
                    </div>
                    <div className={guided ? 'mt-2' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
                      <div className={guided ? 'hidden' : undefined}>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Proposed By
                        </label>
                        <div className="rounded bg-gray-100 px-3 py-2 text-gray-700">
                          {preparedBy.name}
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="signed-by-input"
                          className="mb-1 block text-sm font-medium text-gray-700"
                        >
                          {guided ? (
                            'Name'
                          ) : (
                            <>
                              Signed By <span className="text-red-600">*</span>
                            </>
                          )}
                        </label>
                        <FormField
                          control={form.control}
                          name="acceptedBy"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  id="signed-by-input"
                                  {...field}
                                  className={
                                    guided && showMissing && !acceptedBy.trim()
                                      ? 'border-destructive bg-white'
                                      : 'bg-white'
                                  }
                                  required
                                  type="text"
                                  autoComplete="name"
                                  autoCorrect="off"
                                  style={{ touchAction: 'manipulation', fontSize: '16px' }}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        {guided && showMissing && !acceptedBy.trim() && (
                          <p className="text-destructive mt-1 text-sm">Type your name.</p>
                        )}
                      </div>
                    </div>
                    {guided ? (
                      <p className="text-muted-foreground mt-3 text-xs">
                        {new Date().toLocaleDateString()} · Prepared by {preparedBy.name}
                      </p>
                    ) : (
                      <div className="mt-4">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
                        <div className="rounded bg-gray-100 px-3 py-2 text-gray-700">
                          {new Date().toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </Card>
                </>
              )}

              {guided && guidedPhoto && (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label={guidedPhoto.fileName || 'Photo'}
                  className="fixed inset-0 z-[60] flex flex-col bg-black pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]"
                >
                  <div className="flex justify-end p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-white hover:bg-white/10 hover:text-white"
                      onClick={() => setGuidedPhoto(null)}
                    >
                      Close
                    </Button>
                  </div>
                  <div className="flex min-h-0 flex-1 items-center justify-center p-2">
                    <img
                      src={guidedPhoto.src}
                      alt={guidedPhoto.fileName || 'Photo'}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Spacer for sticky footer */}
              <div className="h-20"></div>
            </div>
          </ScrollArea>

          {/* Sticky Footer with Approve Button */}
          {guided && !isLastGuidedStep ? (
            <div className="flex gap-2 border-t bg-white p-3">
              {guidedStepIndex > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => goToGuidedStep(guidedStepIndex - 1)}
                >
                  Back
                </Button>
              )}
              <Button
                type="button"
                className="flex-1"
                disabled={!canContinueGuided}
                onClick={() => goToGuidedStep(guidedStepIndex + 1)}
              >
                Continue
              </Button>
            </div>
          ) : (
            <div className="border-t bg-white p-3 shadow-lg">
              <div className={guided ? 'flex w-full gap-2' : 'w-full'}>
                {guided && guidedStepIndex > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => goToGuidedStep(guidedStepIndex - 1)}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={guided ? isSubmitting : !isApproveEnabled}
                  onClick={(event) => {
                    if (!guided || isApproveEnabled) return;
                    event.preventDefault();
                    setShowMissing(true);
                    const firstMissing =
                      acceptedServices.length === 0
                        ? 'guided-services'
                        : !acceptTerms
                          ? 'guided-terms'
                          : !signature
                            ? 'signature-canvas'
                            : 'signed-by-input';
                    document
                      .getElementById(firstMissing)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className={
                    guided
                      ? 'flex-1'
                      : 'w-full bg-green-600 py-4 text-base font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500'
                  }
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {!guided && (
                        <svg
                          className="mr-2 h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                      {guided ? 'Approve and sign' : 'Approve Proposal'}
                    </>
                  )}
                </Button>
                {!guided && !isApproveEnabled && !isSubmitting && (
                  <p className="mt-2 text-center text-sm text-gray-500">
                    Select at least one service, accept terms, provide signature, and enter your
                    name
                  </p>
                )}
              </div>
            </div>
          )}
        </form>
      </FormContext>
    </div>
  );
};
